import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IKnowledgeBaseArticle } from "../api/knowledgeBase";

type SelectOptions = { fromHash?: boolean };
type UpdateHashOptions = { slugOverride?: string };

export function useArticleNavigation(
  allArticles: IKnowledgeBaseArticle[] | undefined,
  isCreatingRef: React.MutableRefObject<boolean>
) {
  const location = useLocation();
  const navigate = useNavigate();
  const suppressHashSyncRef = React.useRef(false);
  const [selectedArticleId, setSelectedArticleId] =
    React.useState<number | null>(null);

  const slugToArticleId = React.useMemo(() => {
    const map = new Map<string, number>();
    allArticles?.forEach((article) => {
      if (article.slug) map.set(article.slug, article.id);
    });
    return map;
  }, [allArticles]);

  const getSlugForId = React.useCallback(
    (articleId: number | null) => {
      if (articleId === null || !allArticles) return null;
      const article = allArticles.find((a) => a.id === articleId);
      return article?.slug || null;
    },
    [allArticles]
  );

  const updateHash = React.useCallback(
    (articleId: number | null, options?: UpdateHashOptions) => {
      const slug = options?.slugOverride ?? getSlugForId(articleId);
      const nextHash = articleId ? `#${slug || articleId}` : "";
      if (location.hash === nextHash) return;
      suppressHashSyncRef.current = true;
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: nextHash,
        },
        { replace: true }
      );
    },
    [getSlugForId, location.hash, location.pathname, location.search, navigate]
  );

  const selectArticle = React.useCallback(
    (articleId: number | null, options?: SelectOptions) => {
      setSelectedArticleId(articleId);
      if (options?.fromHash) return;
      updateHash(articleId);
    },
    [updateHash]
  );

  React.useEffect(() => {
    if (isCreatingRef.current) return;
    if (!allArticles || allArticles.length === 0) return;

    const rawHash = location.hash.replace("#", "");
    const decodedHash = decodeURIComponent(rawHash);
    const numericFromHash =
      decodedHash && !Number.isNaN(Number(decodedHash))
        ? Number(decodedHash)
        : null;
    const articleIdFromSlug =
      decodedHash && slugToArticleId.size > 0
        ? slugToArticleId.get(decodedHash) ?? null
        : null;

    const targetArticleId =
      articleIdFromSlug ??
      (numericFromHash !== null
        ? allArticles.find((a) => a.id === numericFromHash)?.id ?? null
        : null);
    const currentArticle = selectedArticleId
      ? allArticles.find((a) => a.id === selectedArticleId)
      : null;

    if (suppressHashSyncRef.current) {
      suppressHashSyncRef.current = false;
      if (selectedArticleId && !currentArticle) {
        selectArticle(null, { fromHash: true });
        updateHash(null);
      }
      return;
    }

    if (targetArticleId && targetArticleId !== selectedArticleId) {
      selectArticle(targetArticleId, { fromHash: true });
      return;
    }

    if (!rawHash && !selectedArticleId) {
      const sortedArticles = [...allArticles].sort(
        (a, b) => a.position - b.position
      );
      const firstRootArticle =
        sortedArticles.find((article) => article.parent_id === null) ??
        sortedArticles[0];

      if (firstRootArticle) {
        selectArticle(firstRootArticle.id, { fromHash: true });
      }
      return;
    }

    if (selectedArticleId && !currentArticle) {
      selectArticle(null, { fromHash: true });
      updateHash(null);
      return;
    }

    if (
      currentArticle &&
      decodedHash &&
      decodedHash !== currentArticle.slug &&
      decodedHash !== String(currentArticle.id)
    ) {
      updateHash(currentArticle.id);
    }
  }, [
    allArticles,
    isCreatingRef,
    location.hash,
    selectArticle,
    selectedArticleId,
    slugToArticleId,
    updateHash,
  ]);

  return { selectedArticleId, selectArticle, updateHash };
}

